import { useState } from 'react';
import { Dropdown, Message } from '@arco-design/web-react';
import { IconCheck, IconDown } from '@arco-design/web-react/icon';
import { useAgentStore } from '../../store/agent';
import StatusTag, { statusLabel } from '../../components/StatusTag';
import { listBreakReasons } from '../../api/agent';
import { AGENT_STATE_COLORS, NEUTRAL } from '../../theme/palette';

/** 四种可手动切换的坐席状态（其余振铃/通话中/话后等只读态由呼叫事件驱动） */
const SWITCHABLE = ['idle', 'busy', 'break', 'offline'];

/**
 * 顶栏坐席状态切换器：示闲/示忙/小休/下线四态单面板树形切换——
 * 小休一级点击展开/收起原因（无二级浮层）；状态选择后即时提交并 Toast 轻反馈。
 * 非可切换态（振铃/通话中/话后处理）渲染只读 StatusTag。
 * 面板状态（open/小休展开/小休原因）自包含，不再提升到 BasicLayout。
 */
export default function AgentStatusTrigger({ forcedState }: { forcedState?: string } = {}) {
  const agent = useAgentStore();
  const displayedState = forcedState || agent.state;
  // 小休原因：懒加载启用项；breakOpen=小休一级展开态（同一 Dropdown 内展开/收起，不弹二级浮层）
  const [breakReasons, setBreakReasons] = useState<{ id: number; name: string }[]>([]);
  const [breakOpen, setBreakOpen] = useState(false);
  // 状态切换器 Dropdown 受控可见：选择最终状态后立即关闭；小休展开/收起不关闭 Dropdown
  const [statusOpen, setStatusOpen] = useState(false);

  const loadBreakReasons = async () => {
    try {
      const list = await listBreakReasons();
      setBreakReasons((Array.isArray(list) ? list : []).filter((r: any) => r.enabled));
    } catch {
      /* 拦截器已提示 */
    }
  };

  const changeStatus = async (state: string, reasonId?: number) => {
    setStatusOpen(false);
    setBreakOpen(false);
    try {
      // 小休必选原因：本地立即回填名称（心跳/WS 兜底补齐）；Toast 轻反馈不抢占注意
      const reasonName =
        state === 'break' && reasonId ? breakReasons.find((r) => r.id === reasonId)?.name : undefined;
      await agent.changeState(state, reasonId, reasonName);
      Message.success({
        content: `已切换为${statusLabel(state)}${reasonName ? ` · ${reasonName}` : ''}`,
        duration: 1500,
      });
    } catch {
      /* 拦截器已提示 */
    }
  };

  // 只读态（振铃/通话中/话后处理等）不提供切换器
  if (!SWITCHABLE.includes(displayedState)) {
    return <StatusTag value={displayedState} />;
  }

  // 树形结构：一级 示闲/示忙 → 小休(+原因二级) → 下线（顺序即 DOM 顺序；label 统一走 statusLabel 唯一源）
  const statusItems = [
    { state: 'idle', label: statusLabel('idle'), color: AGENT_STATE_COLORS.idle },
    { state: 'busy', label: statusLabel('busy'), color: AGENT_STATE_COLORS.busy },
  ];
  const statusPanel = (
    <div className="bcc-status-panel">
      <div className="bcc-status-panel-title">坐席状态</div>
      {statusItems.map((s) => (
        <div
          key={s.state}
          className={`bcc-status-opt${displayedState === s.state ? ' selected' : ''}`}
          onClick={() => void changeStatus(s.state)}
        >
          <span className="bcc-agent-state-dot" style={{ background: s.color }} />
          <span className="bcc-status-opt-label">{s.label}</span>
          {displayedState === s.state && <IconCheck />}
        </div>
      ))}
      {/* 小休：一级状态 + 点击展开/收起二级原因（Chevron 旋转，Dropdown 不关闭） */}
      <div
        className={`bcc-status-opt${displayedState === 'break' ? ' selected' : ''}`}
        onClick={() => {
          setBreakOpen((o) => !o);
          if (breakReasons.length === 0) void loadBreakReasons();
        }}
      >
        <span className="bcc-agent-state-dot" style={{ background: AGENT_STATE_COLORS.break }} />
        <span className="bcc-status-opt-label">{statusLabel('break')}</span>
        <IconDown className={`bcc-status-chevron${breakOpen ? ' open' : ''}`} />
      </div>
      {breakOpen &&
        (breakReasons.length > 0 ? (
          <div className="bcc-status-break-list">
            {breakReasons.map((r) => (
              <div
                key={r.id}
                className={`bcc-status-reason${
                  displayedState === 'break' && agent.breakReasonId === r.id ? ' selected' : ''
                }`}
                onClick={() => void changeStatus('break', r.id)}
              >
                {r.name}
                {displayedState === 'break' && agent.breakReasonId === r.id && <IconCheck />}
              </div>
            ))}
          </div>
        ) : (
          <div className="bcc-status-panel-empty">未配置小休原因（可在「坐席基础设置」维护）</div>
        ))}
      {/* 下线：永远在小休分组之后 */}
      <div
        className={`bcc-status-opt${displayedState === 'offline' ? ' selected' : ''}`}
        onClick={() => void changeStatus('offline')}
      >
        <span className="bcc-agent-state-dot" style={{ background: AGENT_STATE_COLORS.offline }} />
        <span className="bcc-status-opt-label">{statusLabel('offline')}</span>
        {displayedState === 'offline' && <IconCheck />}
      </div>
    </div>
  );

  return (
    <Dropdown
      droplist={statusPanel}
      trigger="click"
      popupVisible={statusOpen}
      onVisibleChange={(visible) => {
        setStatusOpen(visible);
        if (visible) {
          setBreakOpen(true); // 打开即展开小休原因，点原因一次生效
          void loadBreakReasons();
        } else {
          setBreakOpen(false);
        }
      }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label="切换坐席状态"
        className="bcc-header-capsule bcc-agent-state-trigger"
      >
        <span
          className="bcc-agent-state-dot"
          style={{ background: AGENT_STATE_COLORS[displayedState] || NEUTRAL }}
        />
        <span className="bcc-agent-state-main">{statusLabel(displayedState)}</span>
        <IconDown className="bcc-agent-state-arrow" />
      </div>
    </Dropdown>
  );
}
